import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { Table, Button, Modal, Input, Form, Space, Tag, Switch, Upload, Collapse } from 'antd';
import { SearchOutlined, UserOutlined, MailOutlined, PhoneOutlined, EnvironmentOutlined, EditOutlined, FileExcelOutlined, MessageOutlined, PlusOutlined, UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import ReactPaginate from 'react-paginate';
import BASEURL from '../API/Config';
import * as XLSX from 'xlsx';

const Customer = () => {
  const token = localStorage.getItem('token');
  const [customers, setCustomers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form] = Form.useForm();
  const [smsLoading, setSmsLoading] = useState(false);

  // New States for Add and Bulk Upload
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [addForm] = Form.useForm();
  const [file, setFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadReport, setUploadReport] = useState(null);

  const handleCreateCustomer = async (values) => {
    setLoading(true);
    try {
      const res = await fetch(`${BASEURL}/user/createcustomer`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json', 
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify(values),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to create customer');
      Swal.fire('Created!', 'Customer created successfully!', 'success');
      setShowAddModal(false);
      addForm.resetFields();
      fetchCustomers();
    } catch (err) {
      Swal.fire('Error', err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = () => {
    const templateData = [
      {
        firstname: "John",
        lastname: "Doe",
        email: "john@example.com",
        phoneNumber: "9876543210",
        password: "password123",
        pincode: "600001",
        address1: "123 Test Street, Madurai"
      }
    ];
    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, "Customer_Bulk_Upload_Template.xlsx");
  };

  const handleBulkUpload = async () => {
    if (!file) {
      Swal.fire('Warning', 'Please select an Excel file first.', 'warning');
      return;
    }

    setUploadLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${BASEURL}/user/uploadcustomers`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });
      
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to upload customers');

      Swal.fire('Success!', 'Bulk upload completed successfully!', 'success');
      setUploadReport(data.report);
      fetchCustomers();
    } catch (err) {
      Swal.fire('Error', err.message, 'error');
    } finally {
      setUploadLoading(false);
    }
  };

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASEURL}/user/all-users`, {
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setCustomers(data);
      setFiltered(data);
    } catch {
      Swal.fire('Error', 'Failed to load customers.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCustomers(); }, []);

  const handleSearch = (e) => {
    const q = e.target.value.toLowerCase();
    setSearch(q);
    setFiltered(customers.filter(c =>
      (c.firstname || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (c.phoneNumber || '').toString().includes(q)
    ));
  };

  const handleExportExcel = () => {
    if (filtered.length === 0) {
      Swal.fire('Info', 'No customers to export.', 'info');
      return;
    }
    const exportData = filtered.map((c, index) => ({
      "S.No": index + 1,
      "First Name": c.firstname || "",
      "Last Name": c.lastname || "",
      "Email": c.email || "",
      "Phone": c.phoneNumber || "",
      "Address": c.address1 || "",
      "Pincode": c.pincode || "",
      "Status": c.isBlocked ? "Inactive" : "Active"
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Customers");
    XLSX.writeFile(wb, "Customer_Master.xlsx");
  };

  const handleSendOfferSMS = () => {
    Swal.fire({
      title: 'Send Promotional SMS?',
      text: 'Are you sure you want to send the promotional offer SMS campaign to all registered customers? This will dispatch a bulk SMS broadcast and cannot be undone.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#ef4444',
      confirmButtonText: 'Yes, Send Campaign',
      cancelButtonText: 'No, Cancel',
      borderRadius: 8
    }).then(async (result) => {
      if (result.isConfirmed) {
        setSmsLoading(true);
        try {
          const res = await fetch(`${BASEURL}/user/send-offer-sms`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          });
          const data = await res.json();
          if (res.ok && data.success) {
            Swal.fire(
              'Campaign Started!',
              data.message || 'The background Offer SMS campaign has been started successfully.',
              'success'
            );
          } else {
            throw new Error(data.message || 'Failed to start the Offer SMS campaign.');
          }
        } catch (err) {
          Swal.fire('Error', err.message, 'error');
        } finally {
          setSmsLoading(false);
        }
      }
    });
  };


  const openModal = (customer) => {
    setEditId(customer._id);
    form.setFieldsValue({
      firstname: customer.firstname || '',
      lastname: customer.lastname || '',
      email: customer.email || '',
      phoneNumber: customer.phoneNumber || '',
      address1: customer.address1 || '',
      pincode: customer.pincode || '',
      isBlocked: !customer.isBlocked,
    });
    setShowModal(true);
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const res = await fetch(`${BASEURL}/user/edit-user/${editId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...values, isBlocked: !values.isBlocked }),
      });
      if (!res.ok) throw new Error(await res.text());
      Swal.fire('Updated!', 'Customer updated successfully!', 'success');
      setShowModal(false);
      fetchCustomers();
    } catch (err) {
      Swal.fire('Error', err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "S.No",
      key: "index",
      width: 60,
      render: (_, __, i) => <span style={{ color: "#6b7280" }}>{i + 1}</span>,
    },
    {
      title: 'CUSTOMER',
      key: 'name',
      render: (r) => (
        <Space>
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#e8f1ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2874f0', fontWeight: 700, fontSize: 14 }}>
            {(r.firstname || '?')[0].toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: 600, color: '#212121', fontSize: 13 }}>{r.firstname} {r.lastname}</div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>{r.email}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'PHONE',
      dataIndex: 'phoneNumber',
      key: 'phone',
      render: (v) => <span style={{ color: '#374151' }}>{v || '—'}</span>,
    },
    {
      title: 'PINCODE',
      dataIndex: 'pincode',
      key: 'pincode',
      render: (v) => <span style={{ color: '#374151' }}>{v || '—'}</span>,
    },
    {
      title: 'STATUS',
      key: 'status',
      render: (r) => r.isBlocked
        ? <Tag color="red" style={{ borderRadius: 50, fontWeight: 600 }}>Inactive</Tag>
        : <Tag color="green" style={{ borderRadius: 50, fontWeight: 600 }}>Active</Tag>,
    },
    {
      title: 'ACTIONS',
      key: 'actions',
      width: 100,
      render: (r) => (
        <Button size="small" icon={<EditOutlined />} onClick={() => openModal(r)}
          style={{ color: '#2874f0', borderColor: '#2874f0', borderRadius: 4 }} />
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1 }}>Masters</div>
          <h2 style={{ margin: 0, fontFamily: 'Outfit,sans-serif', fontSize: 22, fontWeight: 700, color: '#212121' }}>Customer Master</h2>
        </div>
        <div style={{ background: '#e8f1ff', borderRadius: 8, padding: '8px 16px', display: 'flex', gap: 8, alignItems: 'center' }}>
          <UserOutlined style={{ color: '#2874f0' }} />
          <span style={{ fontWeight: 700, color: '#2874f0', fontSize: 16 }}>{customers.length}</span>
          <span style={{ fontSize: 13, color: '#6b7280' }}>Total Customers</span>
        </div>
      </div>

      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e8ecf0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Input prefix={<SearchOutlined style={{ color: '#9ca3af' }} />} placeholder="Search name, email, phone..."
            value={search} onChange={handleSearch} style={{ width: 300, borderRadius: 4 }} />
          <Button
            type="primary"
            icon={<FileExcelOutlined />}
            onClick={handleExportExcel}
            style={{ background: '#2e7d32', borderColor: '#2e7d32', borderRadius: 4, fontWeight: 600, display: 'inline-flex', alignItems: 'center' }}
          >
            Export to Excel
          </Button>
          <Button
            type="primary"
            icon={<MessageOutlined />}
            onClick={handleSendOfferSMS}
            loading={smsLoading}
            style={{ background: '#d97706', borderColor: '#d97706', borderRadius: 4, fontWeight: 600, display: 'inline-flex', alignItems: 'center' }}
          >
            Send Offer SMS to All
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setShowAddModal(true)}
            style={{ background: '#2874f0', borderColor: '#2874f0', borderRadius: 4, fontWeight: 600, display: 'inline-flex', alignItems: 'center' }}
          >
            Add Customer
          </Button>
          <Button
            type="primary"
            icon={<UploadOutlined />}
            onClick={() => {
              setUploadReport(null);
              setFile(null);
              setShowUploadModal(true);
            }}
            style={{ background: '#4f46e5', borderColor: '#4f46e5', borderRadius: 4, fontWeight: 600, display: 'inline-flex', alignItems: 'center' }}
          >
            Bulk Upload Excel
          </Button>
          <div style={{ marginLeft: 'auto', fontSize: 13, color: '#6b7280' }}>
            Showing: <strong style={{ color: '#212121' }}>{filtered.length}</strong>
          </div>
        </div>
        <Table columns={columns} dataSource={filtered} rowKey="_id" loading={loading}
          pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "30", "40"], showTotal: (t) => `${t} customers` }} size="middle" />
      </div>

      <Modal
        title={<span style={{ fontFamily: 'Outfit,sans-serif', fontWeight: 700 }}>Edit Customer</span>}
        open={showModal} onCancel={() => setShowModal(false)} footer={null} centered width={580}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} style={{ marginTop: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <Form.Item name="firstname" label="First Name">
              <Input prefix={<UserOutlined />} size="large" />
            </Form.Item>
            <Form.Item name="lastname" label="Last Name">
              <Input prefix={<UserOutlined />} size="large" />
            </Form.Item>
            <Form.Item name="email" label="Email">
              <Input prefix={<MailOutlined />} size="large" />
            </Form.Item>
            <Form.Item name="phoneNumber" label="Phone">
              <Input prefix={<PhoneOutlined />} size="large" />
            </Form.Item>
            <Form.Item name="address1" label="Address">
              <Input size="large" />
            </Form.Item>
            <Form.Item name="pincode" label="Pincode">
              <Input prefix={<EnvironmentOutlined />} size="large" />
            </Form.Item>
          </div>
          <Form.Item name="isBlocked" label="Status" valuePropName="checked">
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" style={{ background: '#2874f0' }} />
          </Form.Item>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Button onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={loading}
              style={{ background: '#2874f0', borderColor: '#2874f0', borderRadius: 4, fontWeight: 600 }}>
              Update Customer
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Add Customer Modal */}
      <Modal
        title={<span style={{ fontFamily: 'Outfit,sans-serif', fontWeight: 700 }}>Add New Customer</span>}
        open={showAddModal} onCancel={() => setShowAddModal(false)} footer={null} centered width={580}
      >
        <Form form={addForm} layout="vertical" onFinish={handleCreateCustomer} style={{ marginTop: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <Form.Item name="firstname" label="First Name">
              <Input prefix={<UserOutlined />} size="large" placeholder="First Name" />
            </Form.Item>
            <Form.Item name="lastname" label="Last Name">
              <Input prefix={<UserOutlined />} size="large" placeholder="Last Name" />
            </Form.Item>
            <Form.Item name="email" label="Email" rules={[{ required: true, message: 'Please input the email!' }, { type: 'email', message: 'Please enter a valid email!' }]}>
              <Input prefix={<MailOutlined />} size="large" placeholder="Email (Required)" />
            </Form.Item>
            <Form.Item name="phoneNumber" label="Phone">
              <Input prefix={<PhoneOutlined />} size="large" placeholder="Phone Number" />
            </Form.Item>
            <Form.Item name="password" label="Password" rules={[{ required: true, message: 'Please input the password!' }]}>
              <Input.Password size="large" placeholder="Password (Required)" />
            </Form.Item>
            <Form.Item name="pincode" label="Pincode" rules={[{ pattern: /^\d{6}$/, message: 'Pincode must be exactly 6 digits!' }]}>
              <Input prefix={<EnvironmentOutlined />} size="large" placeholder="Pincode (e.g. 600001)" />
            </Form.Item>
          </div>
          <Form.Item name="address1" label="Address">
            <Input.TextArea rows={2} placeholder="Address Line 1" />
          </Form.Item>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
            <Button onClick={() => setShowAddModal(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={loading}
              style={{ background: '#2874f0', borderColor: '#2874f0', borderRadius: 4, fontWeight: 600 }}>
              Create Customer
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Bulk Upload Excel Modal */}
      <Modal
        title={<span style={{ fontFamily: 'Outfit,sans-serif', fontWeight: 700 }}>Bulk Upload Customers</span>}
        open={showUploadModal} onCancel={() => setShowUploadModal(false)} footer={null} centered width={540}
      >
        <div style={{ marginTop: 16 }}>
          <p style={{ color: '#4b5563', fontSize: 13, lineHeight: 1.5 }}>
            Upload an Excel (.xlsx or .xls) file containing customer details. You can download the template Excel sheet below to format your data correctly.
          </p>

          <Button
            type="dashed"
            icon={<DownloadOutlined />}
            onClick={handleDownloadTemplate}
            style={{ width: '100%', marginBottom: 20, height: 40, color: '#4f46e5', borderColor: '#4f46e5', borderRadius: 6, fontWeight: 600 }}
          >
            Download Excel Template
          </Button>

          <div style={{ border: '2px dashed #cbd5e1', borderRadius: 8, padding: '24px 16px', textAlign: 'center', background: '#f8fafc', marginBottom: 20 }}>
            <UploadOutlined style={{ fontSize: 32, color: '#64748b', marginBottom: 12 }} />
            <div style={{ marginBottom: 12 }}>
              <Upload
                beforeUpload={(file) => {
                  setFile(file);
                  setUploadReport(null);
                  return false; // prevent automatic upload
                }}
                fileList={file ? [file] : []}
                onRemove={() => setFile(null)}
                accept=".xlsx, .xls"
                maxCount={1}
              >
                <Button icon={<UploadOutlined />}>Select Excel File</Button>
              </Upload>
            </div>
            <span style={{ fontSize: 11, color: '#94a3b8' }}>Only .xlsx and .xls files are supported</span>
          </div>

          {uploadReport && (
            <div style={{ marginTop: 20, padding: 16, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0', marginBottom: 16 }}>
              <h4 style={{ margin: '0 0 12px 0', color: '#1e293b', fontWeight: 600, fontSize: 14 }}>Upload Result Report</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', fontSize: 13, marginBottom: 12 }}>
                <div>Processed Rows: <strong style={{ color: '#0f172a' }}>{uploadReport.totalProcessed}</strong></div>
                <div>Successfully Created: <strong style={{ color: '#16a34a' }}>{uploadReport.totalInserted}</strong></div>
                <div>Missing Required Fields: <strong style={{ color: '#dc2626' }}>{uploadReport.missingRequired}</strong></div>
                <div>Duplicates Skipped: <strong style={{ color: '#d97706' }}>{uploadReport.skippedDuplicates?.length || 0}</strong></div>
              </div>
              {uploadReport.skippedDuplicates && uploadReport.skippedDuplicates.length > 0 && (
                <Collapse ghost size="small" style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6 }}>
                  <Collapse.Panel header={<span style={{ fontSize: 12, color: '#475569', fontWeight: 600 }}>View Skipped Emails ({uploadReport.skippedDuplicates.length})</span>} key="1">
                    <div style={{ maxHeight: 100, overflowY: 'auto', fontSize: 12, color: '#64748b', display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {uploadReport.skippedDuplicates.map((email, idx) => (
                        <span key={idx}>{email}</span>
                      ))}
                    </div>
                  </Collapse.Panel>
                </Collapse>
              )}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Button onClick={() => setShowUploadModal(false)}>Close</Button>
            <Button
              type="primary"
              onClick={handleBulkUpload}
              loading={uploadLoading}
              disabled={!file}
              style={{ background: '#4f46e5', borderColor: '#4f46e5', borderRadius: 4, fontWeight: 600 }}
            >
              Upload & Import Customers
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Customer;

